import React, { useEffect, useState, useMemo } from 'react';

import { CheckCircleFill as CheckIcon } from '@styled-icons/bootstrap/CheckCircleFill';
import { PencilFill as PencilIcon } from '@styled-icons/bootstrap/PencilFill';
import { Plus as PlusIcon } from '@styled-icons/bootstrap/Plus';
import { BuildingHouse as BuildingHouseIcon } from '@styled-icons/boxicons-regular/BuildingHouse';
import { Globe as GlobeIcon } from '@styled-icons/boxicons-regular/Globe';
import { Analyse as AnalyseIcon } from '@styled-icons/boxicons-solid/Analyse';
import { Cog as CogIcon } from '@styled-icons/boxicons-solid/Cog';
import { Star as StarIcon } from '@styled-icons/boxicons-solid/Star';
import { Block as BlockIcon } from '@styled-icons/entypo/Block';
import { Warning as WarningIcon } from '@styled-icons/entypo/Warning';
import { DollarSign as DollarSignIcon } from '@styled-icons/fa-solid/DollarSign';
import { Filter as FilterIcon } from '@styled-icons/fa-solid/Filter';
import { Handshake as HandshakeIcon } from '@styled-icons/fa-solid/Handshake';
import { Wrench as WrenchIcon } from '@styled-icons/icomoon/Wrench';
import { SendPlane as SendPlaneIcon } from '@styled-icons/remix-fill/SendPlane';
import { lighten } from 'polished';
import { toast } from 'shared/toast';

import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  ModalFooter,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Flex,
  Box,
  Checkbox,
  Grid,
  GridItem,
  Text,
  Divider,
  Stack,
  SimpleGrid,
} from '@chakra-ui/react';

import { ModalRootProps } from 'components/Modal/Root';

import ColorPickerPopover from './ColorPickerPopover';

type FormErrors = {
  [key: string]: string | boolean;
  type: 'invalid' | 'required';
};

interface ModalFlowStatusAddProps extends ModalRootProps {
  data: {
    flowName?: string;
    description: string;
    color: string;
    default: boolean;
    icon: string;
    blockEdit?: boolean;
  };
}

const ModalFlowStatusAdd: React.FC<ModalFlowStatusAddProps> = ({
  mode = 'add',
  onConfirm,
  handleClose,
  data,
  ...restProps
}) => {
  const [description, setDescription] = useState<string>(data.description);
  const [color, setColor] = useState<string>(data.color);
  const [isDefault, setIsDefault] = useState(data.default || false);
  const [icon, setIcon] = useState<string>(data.icon || '');
  const [isEditBlocked, setIsEditBlocked] = useState(Boolean(data.blockEdit));

  const descriptionRef = React.useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors | undefined>();

  const clearColorError = (): void => {
    setErrors((prev) => {
      if (!prev || !prev.color) return prev;
      const { color: _color, ...rest } = prev;
      return Object.keys(rest).length ? rest : undefined;
    });
  };

  useEffect(() => {
    if (descriptionRef.current) {
      descriptionRef.current.focus();
    }
  }, []);

  const icons = useMemo(
    () => [
      {
        id: 'Globe',
        component: GlobeIcon,
        path: '@styled-icons/boxicons-regular/Globe',
      },
      {
        id: 'Check',
        component: CheckIcon,
        path: '@styled-icons/bootstrap/CheckCircleFill',
      },
      {
        id: 'BuildingHouse',
        component: BuildingHouseIcon,
        path: '@styled-icons/boxicons-regular/BuildingHouse',
      },
      {
        id: 'Analyse',
        component: AnalyseIcon,
        path: '@styled-icons/boxicons-solid/Analyse',
      },
      {
        id: 'Star',
        component: StarIcon,
        path: '@styled-icons/boxicons-solid/Star',
      },
      {
        id: 'Block',
        component: BlockIcon,
        path: '@styled-icons/entypo/Block',
      },
      {
        id: 'SendPlane',
        component: SendPlaneIcon,
        path: '@styled-icons/remix-fill/SendPlane',
      },
      {
        id: 'DollarSign',
        component: DollarSignIcon,
        path: '@styled-icons/fa-solid/DollarSign',
      },
      {
        id: 'Warning',
        component: WarningIcon,
        path: '@styled-icons/entypo/Warning',
      },
      {
        id: 'Cog',
        component: CogIcon,
        path: '@styled-icons/boxicons-solid/Cog',
      },
      {
        id: 'Plus',
        component: PlusIcon,
        path: '@styled-icons/bootstrap/Plus',
      },
      {
        id: 'PencilFill',
        component: PencilIcon,
        path: '@styled-icons/bootstrap/PencilFill',
      },
      {
        id: 'Handshake',
        component: HandshakeIcon,
        path: '@styled-icons/fa-solid/Handshake',
      },
      {
        id: 'Filter',
        component: FilterIcon,
        path: '@@styled-icons/fa-solid/Filter',
      },
      {
        id: 'Wrench',
        component: WrenchIcon,
        path: '@styled-icons/icomoon/Wrench',
      },
    ],
    [],
  );

  const handleConfirm = async (): Promise<void> => {
    const formErrors: FormErrors = {} as FormErrors;

    if (!description) {
      Object.assign(formErrors, { description: true, type: 'required' });
    }

    if (!color) {
      Object.assign(formErrors, { color: true, type: 'required' });
    }

    setErrors(formErrors);
    if (Object.keys(formErrors).length) {
      return;
    }

    setLoading(true);
    try {
      if (onConfirm) {
        onConfirm({
          description,
          color,
          isDefault,
          icon: icon || null,
          blockEdit: isEditBlocked,
        });
      }
      handleClose();
    } catch (err) {
      toast({
        description:
          err.response?.data?.message || 'Houve um erro ao criar o status!',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const selectedColor = color || '#3CAF53';
  const selectedIconBg = lighten(0.6, selectedColor);

  return (
    <Modal size="4xl" {...restProps}>
      <ModalOverlay />
      <ModalContent maxW="1100px" borderRadius="2xl">
        <ModalHeader pb={3}>
          <Box>
            <Text fontSize="2xl" fontWeight="semibold" color="gray.800">
              {mode === 'add' ? 'Criar status' : 'Editar status'}
            </Text>
            <Text fontSize="md" color="gray.600" mt={1}>
              Fluxo: {data.flowName || 'Fluxo de orçamentos'}
            </Text>
          </Box>
        </ModalHeader>

        <ModalCloseButton />
        <Divider />

        <ModalBody px={{ base: 4, md: 6 }} py={6}>
          <Grid
            templateColumns={{ base: '1fr', lg: '1.1fr 0.9fr' }}
            columnGap={{ base: 6, lg: 10 }}
            rowGap={8}
          >
            <GridItem>
              <Text fontSize="lg" fontWeight="semibold" color="gray.800">
                Geral
              </Text>
              <Stack spacing={5} mt={4}>
                <FormControl isInvalid={!!errors?.description}>
                  <FormLabel>Nome</FormLabel>
                  <Input
                    ref={descriptionRef}
                    w="100%"
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleConfirm()}
                  />
                  <FormErrorMessage>Descrição é obrigatória</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors?.color}>
                  <FormLabel>Cor</FormLabel>
                  <Flex gap={3} align="center">
                    <ColorPickerPopover
                      color={color}
                      onColorChange={(newSelectedColor) => {
                        setColor(newSelectedColor);
                        if (newSelectedColor) {
                          clearColorError();
                        }
                      }}
                    >
                      <Button
                        variant="outline"
                        w="100%"
                        h="40px"
                        minH="40px"
                        justifyContent="space-between"
                        rightIcon={<ChevronDownIcon />}
                        borderColor={errors?.color ? 'red.400' : 'gray.200'}
                        bg={errors?.color ? 'red.100' : 'white'}
                        _hover={{
                          borderColor: errors?.color ? 'red.500' : 'gray.300',
                        }}
                        _focusVisible={{
                          boxShadow: errors?.color
                            ? '0 0 0 1px #FC8181'
                            : '0 0 0 1px #CBD5E0',
                        }}
                      >
                        <Flex align="center" gap={3} w="100%">
                          <Box
                            boxSize="18px"
                            borderRadius="full"
                            background={color}
                            borderWidth="1px"
                            borderColor="gray.200"
                          />
                          <Text color="gray.700" fontWeight="medium">
                            {color ? color.toUpperCase() : 'Selecione uma cor'}
                          </Text>
                        </Flex>
                      </Button>
                    </ColorPickerPopover>
                  </Flex>
                  <FormErrorMessage>Cor é obrigatória</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors?.icon}>
                  <FormLabel>Ícone</FormLabel>
                  <SimpleGrid
                    minChildWidth="64px"
                    spacing={3}
                    mt={4}
                    p={2}
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor="gray.100"
                    bg="gray.50"
                  >
                    {icons.map(({ id, path, component: IconComponent }) => (
                      <Flex
                        key={id}
                        direction="column"
                        align="center"
                        justify="center"
                        p={3}
                        borderRadius="lg"
                        cursor="pointer"
                        borderWidth="1px"
                        borderColor={icon === path ? selectedColor : 'gray.200'}
                        bg={icon === path ? selectedIconBg : 'white'}
                        transition="all 0.15s ease"
                        _hover={{
                          borderColor:
                            icon === path ? selectedColor : 'gray.300',
                          boxShadow: 'sm',
                        }}
                        onClick={() => setIcon(path)}
                      >
                        <Box boxSize="22px">
                          <IconComponent
                            size={22}
                            color={icon === path ? selectedColor : '#333'}
                          />
                        </Box>
                      </Flex>
                    ))}
                  </SimpleGrid>

                  <FormErrorMessage>Ícone é obrigatório</FormErrorMessage>
                </FormControl>
              </Stack>
            </GridItem>

            <GridItem>
              <Stack spacing={6}>
                <Box>
                  <Text fontSize="lg" fontWeight="semibold" color="gray.800">
                    Comportamento
                  </Text>
                  <Stack spacing={4} mt={4}>
                    <Box
                      borderWidth="1px"
                      borderColor="gray.200"
                      borderRadius="lg"
                      p={4}
                      bg="white"
                    >
                      <Checkbox
                        isChecked={isDefault}
                        onChange={(e) => {
                          setIsDefault(e.target.checked);
                        }}
                      >
                        Definir como status inicial
                      </Checkbox>

                      <Text fontSize="sm" color="gray.600" mt={2} ml={6}>
                        Orçamentos novos serão criados com este status. Só pode
                        haver um status inicial por fluxo.
                      </Text>
                    </Box>

                    <Box
                      borderWidth="1px"
                      borderColor="gray.200"
                      borderRadius="lg"
                      p={4}
                      bg="white"
                    >
                      <Checkbox
                        isChecked={isEditBlocked}
                        onChange={(e) => {
                          setIsEditBlocked(e.target.checked);
                        }}
                      >
                        Bloquear edições
                      </Checkbox>

                      <Text fontSize="sm" color="gray.600" mt={2} ml={6}>
                        Orçamentos neste status não poderão ter seus dados e/ou
                        valores alterados.
                      </Text>
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            </GridItem>
          </Grid>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" px={{ base: 4, md: 6 }} py={4}>
          <Flex w="100%" justify="flex-end" gap={3}>
            <Button variant="ghost" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              isLoading={loading}
              isDisabled={loading}
              colorScheme="green"
              onClick={handleConfirm}
            >
              {mode === 'add' ? 'Criar status' : 'Salvar alterações'}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalFlowStatusAdd;
